import {useProjectId} from "../ProjectName";
import React, {useContext, useMemo} from "react";
import {ManagerLockContext} from "../ProjectActions";
import {hasPermission, useUser} from "../../../../misc/Permission";
import {sovCellCustomize, sovColumns, usePricingKit2} from "./usePricingKit2";
import {
    centsParseValue,
    Column,
    lookupNestedProp,
    NumberCol, renderCents, RenderStringCol,
    StringCol,
    Table
} from "../../../../misc/scroller/Table";
import {isNullOrUndefined, MarkupEditCell, parseMarkup, priceTableName} from "./Pricing";
import {getAll} from "../../../logistics/routes/useGetAll";
import {api} from "../../../../api/API";
import {PriceRider} from "../../../../api/Pricing";
import {cellInActive} from "./cellconst";
import {PreviousValuePopup} from "../openings/PreviousValuePopup";
import {costEachColumnName, markupColumnName} from "./Const";
import {formatCents} from "nate-react-api-helpers";
import {useCheckboxExtSetupData} from "../../manage/sov/CheckboxExtProvider";
import {RunningTotalContext} from "../changeset/RunningTotalContext";
import {riderSections} from "../../quote/pricing/QuotePriceRiderUtil";

export function Riders(props: {
    sov?: number;
}) {
    const project = useProjectId();
    const {locked} = useContext(ManagerLockContext)
    const u = useUser();

    const kit = usePricingKit2(project);
    const checkboxAllData = useCheckboxExtSetupData();
    const totalCtx = useContext(RunningTotalContext);

    const cellCustomize = useMemo(() => {
        return {
            backgroundColor: (row: PriceRider, c: Column<PriceRider>) => {
                if(!kit.sessionId) return null;

                let changed = false;
                if("isChangedSinceBackup" in c && c.isChangedSinceBackup) {
                    changed = c.isChangedSinceBackup(row)
                } else if(c.editKey) {
                    changed = !!row.backup && lookupNestedProp(row.backup, c.editKey) !== lookupNestedProp(row, c.editKey)
                } else {
                    changed = false;
                }

                if(changed) {
                    return "white"
                }

                return cellInActive;
            },
            onFocus: (row: PriceRider, c: Column<PriceRider>, anchor: any) => {
                if(!kit.sessionId) return null;

                let changed = false;

                if("isChangedSinceBackup" in c && c.isChangedSinceBackup) {
                    changed = c.isChangedSinceBackup(row)
                } else if(c.editKey) {
                    changed = !!row.backup && lookupNestedProp(row.backup, c.editKey) !== lookupNestedProp(row, c.editKey)
                } else {
                    changed = false;
                }

                if(changed) {
                    const value = row.backup as any;
                    return (
                        <PreviousValuePopup anchor={anchor} width={c.width} value={c.render(value, c)} />
                    )
                }

                return null;
            }
        }
    }, [kit.sessionId]);

    if(!u) return null;
    if(kit.Loading) return <>{kit.Loading}</>

    return (
        <>
            <Table<PriceRider>
                name={priceTableName(project, "rider")}
                globalPrefsName={priceTableName(0, "rider")}
                cellCustomize={props.sov ? sovCellCustomize(props.sov) : cellCustomize}
                locked={locked && !hasPermission(u, "EditShopDrawingWhenLockedToManager")}
                fetch={async (ctx) => {
                    const data = checkboxAllData(getAll(ctx, offset =>
                        api.pricing.listRiders({
                            project: project,
                            offset,
                            forSovLink: !!props.sov,
                        })
                    ))

                    if(props.sov) {
                        totalCtx.capture(Promise.resolve([]), Promise.resolve([]), data);
                    }

                    return data;
                }}
                columns={[
                    ...sovColumns(props.sov),
                    RenderStringCol("Section", (dt: PriceRider) => {
                        const s = riderSections.find(s => s.key === dt.sectionKey);
                        if(s) return s.sectionName;
                        return dt.sectionKey;
                    }, 150),
                    StringCol("Name", "name", 150),
                    NumberCol("Qty", "qty"),
                    {
                        name: costEachColumnName,
                        editable: {
                            type: "number",
                            parseValue: centsParseValue,
                        },
                        render: (row: PriceRider) => renderCents(row, "unitCostCents"),
                        copy: (row: PriceRider) => renderCents(row, "unitCostCents"),
                        editKey: "unitCostCents" as any,
                        alignRight: true,
                        width: 80,
                    },
                    {
                        name: markupColumnName,
                        isChangedSinceBackup: (row: PriceRider) => {
                            if(!row.backup) return false;
                            return row.markupCents !== row.backup?.markupCents || row.markupPercent !== row.backup?.markupPercent
                        },
                        editable: {
                            type: "custom",
                            copy: (dt: PriceRider) => {
                                if(!isNullOrUndefined(dt.markupPercent)) return dt.markupPercent + "%"
                                if(!!dt.markupCents) return "$" + formatCents(dt.markupCents)
                                return "auto"
                            },
                            paste: (rw: PriceRider, value: string) => {
                                Object.assign(rw, parseMarkup(value))
                            },
                            render: MarkupEditCell as any,
                        },

                        render: (dt: PriceRider) => {
                            if(!isNullOrUndefined(dt.markupPercent)) return dt.markupPercent + "%"
                            if(!!dt.markupCents) return "$" + formatCents(dt.markupCents)
                            return "auto"
                        },
                        alignRight: true,
                        width: 80,
                    }, {
                        name: "Unit Price",
                        render: (dt: PriceRider) => formatCents(dt.extendedPrice / fixZero(dt.qty)),
                        alignRight: true,
                        width: 80,
                    }, {
                        name: "Price",
                        editable: {
                            type: "number",
                            parseValue: centsParseValue,
                        },
                        alignRight: true,
                        editKey: "extendedPrice",
                        render: (dt: PriceRider) => formatCents(dt.extendedPrice),
                        copy: (dt: PriceRider) => formatCents(dt.extendedPrice),
                    },
                ]}
                columnDeps={[u, kit.markup, props.sov]}
                fetchDeps={[project, props.sov]}
            />
        </>
    )
}

function fixZero(value: number) {
    if(value === 0) return 1;
    return value;
}